import { Col, Popconfirm, Row } from 'antd';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ReactComponent as CloseIcon } from '../../../../../../assets/icons/close-icon.svg';
import { ReactComponent as PencilIcon } from '../../../../../../assets/icons/pencil.svg';
import { ReactComponent as TickIcon } from '../../../../../../assets/icons/tick.svg';
import { routeFileSelector } from '../../../../../../utilz/i18n';
import IconButton from '../../../../../components/Buttons/IconButton';
import SvgConverter from '../../../../../components/Common/SvgConverter';
import FormCheckBox from '../../../../../components/Inputs/FormCheckBox';
import HiddenFormInput from '../../../../../components/Inputs/HiddenFormInput';
import SecondaryLabel from '../../../../../components/Labels/SecondaryLabel';
import AccessorialSelector from '../../../../../components/Selectors/AsynchSearch/AccessorialSelector';

const AccessorialCard = memo(
  ({
    name,
    form = {},
    accessorialDetails = {},
    handleDataChange,
    defaultValues = {},
    handleRemove,
    onCheckboxChange,
  }) => {
    const editMode = Boolean(accessorialDetails?.editMode);
    const [error, setError] = useState(false);
    const setEditMode = (editMode) => {
      handleDataChange({ editMode }, name);
    };
    const { pathname: activeTab } = useLocation();
    const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
    const { t } = useTranslation([namespace, 'translation']);

    const handleCancel = () => {
      if (accessorialDetails?.isNew || !editMode) {
        handleRemove(name);
      } else {
        handleDataChange({ ...defaultValues, editMode: false }, name);
      }
    };

    const handleSave = async () => {
      if (accessorialDetails?.codeId) {
        handleDataChange({ ...accessorialDetails, editMode: false, isNew: false }, name, true);
        setError(false);
      } else {
        setError(t('translation:getQuote.rateShopDetails.additionalDetails.error'));
      }
    };

    const handleChange = (data) => {
      handleDataChange({ codeId: data?.codeId, codeDesc: data?.codeDesc }, name);
    };

    return (
      <div className="pb-10 pt-2 px-15 border-bottom-border">
        <Row gutter={20} align="middle" className="flex-nowrap">
          <Col className="mt-5">
            <FormCheckBox
              className="m-0 w-100 d-block"
              checkBoxClass="normal-checkbox"
              name={[name, 'isChecked']}
              label={false}
              disabled={accessorialDetails.accChargeType === 'FUE'}
              {...(onCheckboxChange
                ? {
                    onChange: onCheckboxChange(accessorialDetails.accChargeType),
                  }
                : {})}
            />
          </Col>
          <Col className="flex-1">
            <AccessorialSelector
              margin="m-0"
              hasError={Boolean(error)}
              form={form}
              name="lookupKey"
              label={t('translation:getQuote.additionalDetails.accessorialName.label')}
              editMode={editMode}
              defaultOptions={
                !accessorialDetails?.isNew
                  ? [
                      {
                        codeId: accessorialDetails?.accChargeType,
                        codeDesc: accessorialDetails?.codeDesc,
                      },
                    ]
                  : []
              }
              inputValue={accessorialDetails?.codeId}
              onChange={handleChange}
            />
            <HiddenFormInput
              className="lh-0"
              name={[name, 'codeId']}
              rules={[
                {
                  required: true,
                  message: t('translation:getQuote.additionalDetails.error'),
                },
              ]}
            />
            {error && <SecondaryLabel className="text-invalidInput f-14">{error}</SecondaryLabel>}
          </Col>
          {accessorialDetails.accChargeType !== 'FUE' && accessorialDetails?.showActions ? (
            <Col className="mt-5">
              <Row gutter={10} align="middle">
                <Col>
                  {!editMode ? (
                    <IconButton onClick={() => setEditMode(true)}>
                      <SvgConverter Icon={PencilIcon} color="header" />
                    </IconButton>
                  ) : (
                    <IconButton onClick={handleSave}>
                      <SvgConverter Icon={TickIcon} color="success" />
                    </IconButton>
                  )}
                </Col>
                <Col>
                  <Popconfirm
                    placement="topRight"
                    title={
                      accessorialDetails?.editMode
                        ? t('translation:default.fields.modal.label')
                        : t('translation:default.fields.modal.labelTwo')
                    }
                    onConfirm={handleCancel}
                    okText={t('translation:default.fields.modal.okText')}
                    cancelText={t('translation:default.options.no')}
                    overlayClassName="delete-popup-container"
                  >
                    <IconButton>
                      <SvgConverter Icon={CloseIcon} color={!editMode ? 'header' : 'danger'} />
                    </IconButton>
                  </Popconfirm>
                </Col>
              </Row>
            </Col>
          ) : (
            <></>
          )}
        </Row>

        <HiddenFormInput name={[name, 'codeDesc']} />
        <HiddenFormInput name={[name, 'editMode']} />
        <HiddenFormInput name={[name, 'accChargeAmount']} />
        <HiddenFormInput name={[name, 'disabled']} />
        <HiddenFormInput name={[name, 'showActions']} />
      </div>
    );
  },
);

export default AccessorialCard;
